import { categories } from "utils/enclosure";
import { getComponentsSortedByRoom, sortRoomsByName } from "utils/roomByRoom";
import { CENTRAL_EQUIP_TYPES } from "./constants";
import { 
    convertGain, 
    formatArea, 
    formatLength, 
    formatTemp, 
    formatAirFlow, 
    getMeasurementUnits,
    formatVolume,
} from "utils/displayMeasurements";

export * from "./warnings-errors";
export * from "./constants";

/* Storey/Room/Component Functions */
export const getStoreyCategories = (storeysId) => categories(storeysId);

export const getCatOptions = (storeysId) => getStoreyCategories(storeysId).reduce((acc, { id, name }) => {
    return [
        ...acc,
        {
            label: name,
            value: id,
        },
    ];
}, []);

export const getStoreyNameFromId = (storeysId, selectedStorey) => getStoreyCategories(storeysId).find(category => category.id === selectedStorey)?.name || "";

export const getRoomDataFunction = (roomSummary, selectedStorey) => (roomId) => roomSummary[selectedStorey]?.[roomId] || {};

export const findLargestRoom = (selectedRooms, getRoomData) => {
    return selectedRooms.reduce((largest, roomId) => {
        const roomData = getRoomData(roomId);
        return roomData?.area > largest?.area ? roomData : largest;
    }, { area: 0 });
};

// Array of [roomId, roomData]
export const getRoomsInStorey = (roomSummary = {}, selectedStorey = "") => {
    return Object.entries(roomSummary[selectedStorey] || {})
        .filter(([roomId, _]) => !roomId.includes("-default-"))
        .sort(sortRoomsByName);
}

// Objects of { roomId: { componentData } }
export const getComponentsInStorey = (components = {}, intComponents = {}, selectedStorey) => {
    const sortedComponents = getComponentsSortedByRoom(components) || {};
    const sortedIntComponents = getComponentsSortedByRoom(intComponents) || {};

    return {
        componentsInStorey: sortedComponents?.[selectedStorey] || {},
        intComponentsInStorey: sortedIntComponents?.[selectedStorey] || {},
    };
}

// Objects of { componentData }
export const getComponentsInRoom = (componentsInStorey = {}, intComponentsInStorey = {}, roomId) => {
    return {
        componentsInRoom: componentsInStorey?.[roomId] || {},
        intComponentsInRoom: intComponentsInStorey?.[roomId] || {},
    };
}

export const getMainComponents = (roomComponents) => Object.entries(roomComponents).filter(
    ([, { type }]) => !["floorHeader", "window", "door"].includes(type)
);

export const generateZoneLabel = (roomSummary, storeysId, selectedStorey, selectedRooms) => {
    const getRoomData = getRoomDataFunction(roomSummary, selectedStorey);
    const storeyName = getStoreyNameFromId(storeysId, selectedStorey);
    const largestRoom = findLargestRoom(selectedRooms, getRoomData);

    return [storeyName, largestRoom?.name].join(" - ");
}

export const findDuplicateZoneLabels = (coolingZones, generatedLabel, zoneId) => {
    return Object.entries(coolingZones).filter(([key, { label = "" }]) => {
        const zoneLabelWithoutNumber = label.replace(/\s*\(\d+\)/, "");
        return (zoneLabelWithoutNumber === generatedLabel) && (key !== zoneId);
    });
};

export const makeZoneLabelUnique = (coolingZones, generatedLabel, zoneId) => {
    const numDuplicateLabels = findDuplicateZoneLabels(coolingZones, generatedLabel, zoneId).length;
    return (numDuplicateLabels > 0) ? `${generatedLabel} (${numDuplicateLabels + 1})` : generatedLabel;
}

export const getZoneComponents = (componentsInStorey, intComponentsInStorey, selectedRooms, selectedWalls = []) => {
    const zoneComponents = selectedRooms.reduce((acc, roomId) => {
        const { componentsInRoom, intComponentsInRoom } = getComponentsInRoom(componentsInStorey, intComponentsInStorey, roomId);
        const allRoomComponents = { ...componentsInRoom, ...intComponentsInRoom };
        const selectedComponents = Object.entries(allRoomComponents).filter(([componentId, { type }]) => (type !== "wall") || (selectedWalls.includes(componentId)));
        return [...acc, ...selectedComponents];
    }, []);

    return zoneComponents.reduce((acc, [componentId, componentData]) => {
        return [
            ...acc,
            {
                id: componentId,
                componentId: componentId,
                componentType: componentData.type,
                componentLabel: componentData.label,
                isInterior: componentData.isInterior,
                roomId: componentData.roomId,
                catId: componentData.category,
            }
        ];
    }, []);
}

export const componentHasWall = (type) => ["wall", "basement", "crawlspace"].includes(type);

// Calculate the covered wall perimeter of a given room within the zone.
// Unselected rooms count every wall. Selected rooms count only selectedWalls.
export const calculateCoveredPerimeter = (componentsInRoom = {}, selectedWalls = [], roomIsSelected = true) => {
    let perimeter = 0;

    Object.entries(componentsInRoom).forEach(([componentId, componentData]) => {
        const componentType = componentData?.type;

        if (["basement", "crawlspace"].includes(componentType)) {
            perimeter += parseFloat(componentData?.exposedSurfacePerimeter || 0);
            return;
        }

        if (componentType === "wall" && (!roomIsSelected || selectedWalls.includes(componentId))) {
            perimeter += parseFloat(componentData?.measurements?.perimeter || 0);
        }
    });

    return perimeter;
};

export const detectCentralAirSystem = (coolingSystem) => {
    return Object.values(coolingSystem)
        .some(({ equipmentType = {} }) => CENTRAL_EQUIP_TYPES.includes(equipmentType?.id));
};

export const groupResultsByZone = (results) => {
    const {
        runId = "",
        upgradePackageId = null,
        caseIds = [],
        status = "",
        updatedAt = "",
        createdAt = "",
        completedCaseIds = [],
        error = null,
        calculationVersion = null,
        cases = {}
    } = results;

    return caseIds.reduce((caseAcc, caseId) => {
        const { upgradePackageId, zoneIds, zones } = cases[caseId];

        return zoneIds.reduce((zoneAcc, zoneId) => ({
            ...zoneAcc,
            [zoneId]: {
                data: zones[zoneId],
                upgradePackageId,
                updatedAt,
            },
        }), caseAcc);
    }, {});
};

export const calculateSczAirFlowRate = (coolingZoneOccupants, homeHasCentralAirSystem, systemServesZone, designAirHandlingCirculationRate, zoneArea) => {
    // 1) In a home without central AHU, 3.5 L/s per occupant
    let sczVentilationAirFlowRate = 3.5 * coolingZoneOccupants;

    // 2) In a home with central AHU, the greater of 3.5 L/s per occupant or what's calculated in (3) when it serves the zone
    if (homeHasCentralAirSystem && !systemServesZone) {
        sczVentilationAirFlowRate = sczVentilationAirFlowRate;
    }

    // 3) If the central AHU serves the zone, the greater of designAirHandlingCirculationRate or 2.5 L/s/m2
    if (homeHasCentralAirSystem && systemServesZone) {
        const designCirculationRate = designAirHandlingCirculationRate || 0;
        sczVentilationAirFlowRate = Math.max(sczVentilationAirFlowRate, designCirculationRate, 2.5 * zoneArea);
    }

    return sczVentilationAirFlowRate;
}

export const getAccordionDataFromZone = (zone, minArea, areaDisplayUnits, perimeterDisplayUnits) => {
    return {
        selectedStorey: zone?.selectedStorey,
        selectedRooms: zone?.selectedRooms,
        selectedWalls: zone?.selectedWalls,
        measurements: {
            totalFloorArea: zone?.measurements?.totalFloorArea,
            minArea,   
        },
        convertedMeasurements: {
            formattedTotalArea: formatArea(zone?.measurements?.totalFloorArea, areaDisplayUnits),
            formattedTotalPerimeter: formatLength(zone?.measurements?.totalWallPerimeter, perimeterDisplayUnits),
        },
    };
};

export const getAccordionDataFromResults = (results, minArea, areaDisplayUnits, volumeDisplayUnits) => {
    return {
        selectedStorey: results?.selectedStorey,
        selectedRooms: results?.selectedRooms,
        selectedWalls: results?.selectedWalls,
        measurements: {
            totalFloorArea: results?.area,
            minArea,
        },
        convertedMeasurements: {
            formattedTotalArea: formatArea(results?.area, areaDisplayUnits),
            formattedTotalVolume: formatVolume(results?.volume, volumeDisplayUnits),
        }
    };
}

/* Export PDF Functions */
const MAX_PDF_NOTES = 5;

export const defaultAttachedDocsNotes = Array.from({ length: MAX_PDF_NOTES }, () => "");

const getDesignatedRoomList = (roomLabels) =>
    roomLabels.map(({ roomId = "", label = "" }) => label || roomId);

const getAssemblyList = (components) =>
    components.map(({ compId = "", label = "" }) => label || compId);

export const getPDFDisplayData = (results, displayUnits, lang) => {
    const {
        buildingLocation = {},
        roomLabels = [],
        totalSensLatHeatGain = 0,
        designConditions: {
            weatherLocation = "",
            cooling = {},
        } = {},
        sczVentilationAirFlowRate = 0,
        hrv = "No",
        hrv_fr = "Non",
        hrvAtre = 0,
        area: areaInMetric = 0,
        interiorComponents = [],
        exteriorComponents = [],
    } = results;

    const {
        airFlowDisplayUnits,
        gainDisplayUnits,
        areaDisplayUnits,
        tempDisplayUnits,
    } = getMeasurementUnits(displayUnits);

    // Compliance
    const designatedRooms = getDesignatedRoomList(roomLabels);
    const minCoolingCapacity = convertGain(totalSensLatHeatGain, gainDisplayUnits);

    // Design Conditions
    const outdoorTemp = formatTemp(cooling.outdoorTemp, tempDisplayUnits);
    const indoorTemp = formatTemp(cooling.indoorTemp, tempDisplayUnits);
    const adjacentTemp = formatTemp(cooling.adjacentTemp, tempDisplayUnits);
    const ventilationRate = formatAirFlow(sczVentilationAirFlowRate, airFlowDisplayUnits);
    const hrvTranslation = (lang === "en") ? hrv : hrv_fr;
    const area = formatArea(areaInMetric, areaDisplayUnits);

    // Building Components & Assemblies
    const outsideFacingAssemblies = getAssemblyList(exteriorComponents);
    const adjacentSpacesAssemblies = getAssemblyList(interiorComponents);

    return ({
        buildingLocation,
        compliance: {
            designatedRooms,
            minCoolingCapacity,
        },
        designConditions: {
            cooling: {
                outdoorTemp,
                indoorTemp,
                adjacentTemp,
            },
            weatherLocation,
            sczVentilationAirFlowRate: ventilationRate,
            hrv: hrvTranslation,
            hrvAtre,
            area,
        },
        outsideFacingAssemblies,
        adjacentSpacesAssemblies,
    });
}